import { memo, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { CustomButton } from '../../../../../components';
import {
  selectScheduleClassIdToObject,
  selectScheduleConstraints,
  selectScheduleData,
  selectScheduleMutationBlocked,
  selectScheduleSelectedMatchIds,
  selectScheduleBulkAdjustEligible,
  selectScheduleTournamentInfo,
  selectUnscheduledMatches,
  selectBulkSelectionScheduledIds,
  selectBulkSelectionUnscheduledIds,
  selectBulkSelectionHasScheduled,
} from '../../../../../features/schedule/scheduleSelectors';
import { applyBulkScheduleTransformAction, bulkRemoveMatchesFromScheduleAction } from '../../../../../features/schedule/scheduleActions';
import {
  hasBulkOpsEffect,
  simulateBulkScheduleTransform,
  type BulkAdjustOps,
  type BulkSkipReason,
} from '../../../../../features/schedule/scheduleBulkTransform';
import { getTournamentTimezone } from '../schedule.helpers';
import { MAX_SCHEDULE_BULK_SELECTION } from '../../../../../features/schedule/scheduleSelectionConstants';
import { useDebounce } from '../../../../../utils/hooks';
import './ScheduleBulkAdjustModal.scss';

const SKIP_REASON_KEYS: Record<BulkSkipReason, string> = {
  cap: 'scheduleBulkAdjustSkipCap',
  cross_day: 'scheduleBulkAdjustSkipCrossDay',
  out_of_courts: 'scheduleBulkAdjustSkipOutOfCourts',
  out_of_day_bounds: 'scheduleBulkAdjustSkipOutOfDayBounds',
  not_scheduled: 'scheduleBulkAdjustSkipNotScheduled',
};

const PREVIEW_DEBOUNCE_MS = 200;

export type ScheduleBulkAdjustModalProps = {
  show: boolean;
  onClose: () => void;
};

function parseBulkOps(
  shiftMinutes: string,
  shiftCourts: string,
  setDuration: string
): BulkAdjustOps {
  const parsed: BulkAdjustOps = {};
  const mins = Number(shiftMinutes);
  const courts = Number(shiftCourts);
  const dur = Number(setDuration);
  if (shiftMinutes.trim() !== '' && Number.isFinite(mins)) parsed.shiftMinutes = mins;
  if (shiftCourts.trim() !== '' && Number.isFinite(courts)) parsed.shiftCourtColumns = courts;
  if (setDuration.trim() !== '' && Number.isFinite(dur)) parsed.setDurationMinutes = dur;
  return parsed;
}

export const ScheduleBulkAdjustModal = memo(function ScheduleBulkAdjustModal({
  show,
  onClose,
}: ScheduleBulkAdjustModalProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const dispatch = useAppDispatch();

  const schedule = useAppSelector(selectScheduleData);
  const scheduleConstraints = useAppSelector(selectScheduleConstraints);
  const unscheduledMatches = useAppSelector(selectUnscheduledMatches);
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const scheduledIds = useAppSelector(selectBulkSelectionScheduledIds);
  const unscheduledIds = useAppSelector(selectBulkSelectionUnscheduledIds);
  const bulkSelectionHasScheduled = useAppSelector(selectBulkSelectionHasScheduled);
  const bulkAdjustEligible = useAppSelector(selectScheduleBulkAdjustEligible);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);
  const classIdToObject = useAppSelector(selectScheduleClassIdToObject);

  const [shiftMinutes, setShiftMinutes] = useState('');
  const [shiftCourts, setShiftCourts] = useState('');
  const [setDuration, setSetDuration] = useState('');

  const debouncedShiftMinutes = useDebounce(shiftMinutes, PREVIEW_DEBOUNCE_MS);
  const debouncedShiftCourts = useDebounce(shiftCourts, PREVIEW_DEBOUNCE_MS);
  const debouncedSetDuration = useDebounce(setDuration, PREVIEW_DEBOUNCE_MS);

  const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);

  const ops = useMemo(
    () => parseBulkOps(shiftMinutes, shiftCourts, setDuration),
    [shiftMinutes, shiftCourts, setDuration]
  );

  const previewOps = useMemo(
    () => parseBulkOps(debouncedShiftMinutes, debouncedShiftCourts, debouncedSetDuration),
    [debouncedShiftMinutes, debouncedShiftCourts, debouncedSetDuration]
  );

  const preview = useMemo(() => {
    if (!show || !schedule || !scheduleConstraints || !hasBulkOpsEffect(previewOps)) return null;
    return simulateBulkScheduleTransform({
      schedule,
      unscheduledMatches,
      targetMatchIds: selectedMatchIds,
      ops: previewOps,
      constraints: scheduleConstraints,
      timeZone,
      classIdToObject,
      mode: 'preview',
    });
  }, [
    show,
    schedule,
    scheduleConstraints,
    previewOps,
    unscheduledMatches,
    selectedMatchIds,
    timeZone,
    classIdToObject,
  ]);

  const handleUnschedule = () => {
    if (!bulkAdjustEligible || scheduleMutationBlocked || !bulkSelectionHasScheduled) return;
    void dispatch(bulkRemoveMatchesFromScheduleAction(scheduledIds, t)).then((ok) => {
      if (ok) onClose();
    });
  };

  const handleApply = () => {
    if (!bulkAdjustEligible || !hasBulkOpsEffect(ops) || scheduleMutationBlocked) return;
    if (!schedule || !scheduleConstraints) return;

    const applyPreview = simulateBulkScheduleTransform({
      schedule,
      unscheduledMatches,
      targetMatchIds: selectedMatchIds,
      ops,
      constraints: scheduleConstraints,
      timeZone,
      classIdToObject,
      mode: 'apply',
    });

    const scheduledIdSet = new Set(scheduledIds);
    const relevantWarnings = applyPreview.warnings.filter((w) =>
      w.matchIds.some((id) => scheduledIdSet.has(id))
    );

    if (relevantWarnings.length > 0) {
      const ok = window.confirm(
        t(prefix + 'scheduleBulkAdjustConfirmWarnings', {
          count: relevantWarnings.length,
          defaultValue:
            '{{count}} scheduling warnings detected. Apply anyway?',
        })
      );
      if (!ok) return;
    }

    void dispatch(
      applyBulkScheduleTransformAction(selectedMatchIds, ops, t, applyPreview)
    ).then((ok) => {
      if (ok) onClose();
    });
  };

  const selectionCount = selectedMatchIds.length;
  const atCap = selectionCount >= MAX_SCHEDULE_BULK_SELECTION;
  const showShiftFields = scheduledIds.length > 0;
  const isMixedSelection = scheduledIds.length > 0 && unscheduledIds.length > 0;

  return (
    <Modal show={show} onHide={onClose} centered className="schedule-bulk-adjust-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {t(prefix + 'scheduleBulkAdjustTitle', {
            count: selectionCount,
            defaultValue: 'Adjust {{count}} matches',
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {scheduleMutationBlocked && (
          <div className="alert alert-warning py-2 small mb-3">
            {t(
              prefix + 'scheduleBulkAdjustBlocked',
              'Publish or enable free edit before adjusting the schedule.'
            )}
          </div>
        )}
        {atCap && (
          <div className="alert alert-info py-2 small mb-3">
            {t(prefix + 'scheduleBulkAdjustCapNotice', {
              max: MAX_SCHEDULE_BULK_SELECTION,
              defaultValue: 'Selection is capped at {{max}} matches per bulk operation.',
            })}
          </div>
        )}
        {isMixedSelection && (
          <div className="alert alert-info py-2 small mb-3">
            {t(
              prefix + 'scheduleBulkAdjustMixedSelectionHint',
              'Time and court adjustments apply to scheduled matches only. Duration applies to all selected matches.'
            )}
          </div>
        )}
        <Form>
          {showShiftFields && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t(prefix + 'scheduleBulkAdjustShiftMinutes', 'Shift start (minutes)')}</Form.Label>
                <Form.Control
                  type="number"
                  value={shiftMinutes}
                  onChange={(e) => setShiftMinutes(e.target.value)}
                  placeholder="±30"
                  disabled={scheduleMutationBlocked}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t(prefix + 'scheduleBulkAdjustShiftCourts', 'Shift courts (columns)')}</Form.Label>
                <Form.Control
                  type="number"
                  value={shiftCourts}
                  onChange={(e) => setShiftCourts(e.target.value)}
                  placeholder="±1"
                  disabled={scheduleMutationBlocked}
                />
              </Form.Group>
            </>
          )}
          <Form.Group className="mb-3">
            <Form.Label>{t(prefix + 'scheduleBulkAdjustSetDuration', 'Set duration (minutes)')}</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={setDuration}
              onChange={(e) => setSetDuration(e.target.value)}
              disabled={scheduleMutationBlocked}
            />
          </Form.Group>
        </Form>
        {preview && hasBulkOpsEffect(previewOps) && (
          <div className="schedule-bulk-adjust-modal__preview small">
            {preview.skipped.length > 0 && (
              <div className="mb-2">
                <div className="fw-semibold">{t(prefix + 'scheduleBulkAdjustSkipped', 'Skipped')}</div>
                <ul className="mb-0 ps-3">
                  {preview.skipped.map((s) => (
                    <li key={`${s.matchId}-${s.reason}`}>
                      #{s.matchId}: {t(prefix + SKIP_REASON_KEYS[s.reason])}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.courtOverlaps.length > 0 && (
              <div className="text-warning mb-2">
                {t(prefix + 'scheduleBulkAdjustPreviewOverlaps', {
                  count: preview.courtOverlaps.length,
                  defaultValue: '{{count}} court overlap(s) detected',
                })}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="schedule-bulk-adjust-modal__footer">
        <CustomButton
          variant="outline-danger"
          cb={handleUnschedule}
          btnText={t(prefix + 'scheduleBulkUnscheduleToolbar', 'Bulk unschedule')}
          disabled={scheduleMutationBlocked || !bulkAdjustEligible || !bulkSelectionHasScheduled}
        />
        <div className="d-flex gap-2 ms-auto">
          <CustomButton variant="outline-secondary" cb={onClose} btnText={t(prefix + 'scheduleBulkAdjustCancel', 'Cancel')} />
          <CustomButton
            variant="primary"
            cb={handleApply}
            btnText={t(prefix + 'scheduleBulkAdjustApply', 'Apply')}
            disabled={scheduleMutationBlocked || !hasBulkOpsEffect(ops) || !bulkAdjustEligible}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
});
